import { Loader2 } from 'lucide-react';
import clsx from 'clsx';

/**
 * The indicator for work that replaces something already on screen —
 * re-running a search, reloading a list after a change.
 *
 * Distinct from a skeleton on purpose. A skeleton says "there is nothing here
 * yet"; replacing populated content with one throws away what the reader was
 * looking at and makes every refetch feel like a page load. This leaves the
 * stale data in place, dimmed slightly, and says quietly that fresher data is
 * on the way.
 */
export function RefreshingBar({ active }: { active: boolean }) {
  return (
    <div
      className="relative h-px w-full overflow-hidden bg-transparent"
      role="status"
      aria-live="polite"
      aria-label={active ? 'Updating' : undefined}
    >
      {active && (
        <span className="absolute inset-y-0 left-0 w-2/5 animate-sweep bg-gradient-to-r from-transparent via-accent to-transparent" />
      )}
    </div>
  );
}

/** Wraps content that is being refreshed, holding it visible but muted. */
export function RefreshingOverlay({
  active,
  children,
}: {
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={clsx(
        'transition-opacity duration-200',
        active && 'pointer-events-none opacity-55',
      )}
      aria-busy={active || undefined}
    >
      {children}
    </div>
  );
}

/** Inline spinner for a search field or a button label. */
export function InlineSpinner({ className }: { className?: string }) {
  return <Loader2 className={clsx('h-3 w-3 animate-spin text-accent', className)} />;
}
